import { useEffect, useState } from "react";
import { Home, Eye, MessageSquare, MessageCircle, Star, ExternalLink, ChevronDown } from "lucide-react";
import api from "@/lib/api";
import OwnerLayout from "@/components/owner/OwnerLayout";

const KPIS = [
  { key: "properties", label: "Strutture", icon: Home, color: "text-brand-plum", bg: "bg-brand-warm" },
  { key: "visits", label: "Visite mini-sito", icon: Eye, color: "text-brand-teal", bg: "bg-brand-tealLight" },
  { key: "inquiries", label: "Clic Verifica disponibilità", icon: MessageSquare, color: "text-brand-gold", bg: "bg-amber-50" },
  { key: "whatsapp", label: "Clic WhatsApp", icon: MessageCircle, color: "text-[#25D366]", bg: "bg-[#e7f9ee]" },
  { key: "avg_rating", label: "Valutazione media", icon: Star, color: "text-brand-gold", bg: "bg-amber-50" },
];

function Metric({ label, value }) {
  return (
    <div className="text-center">
      <p className="font-serif text-2xl text-brand-plum">{value}</p>
      <p className="text-[11px] uppercase tracking-wide text-brand-plum/40 mt-0.5">{label}</p>
    </div>
  );
}

export default function OwnerDashboard() {
  const [data, setData] = useState(null);
  const [openReviews, setOpenReviews] = useState(null);

  useEffect(() => { api.get("/owner/dashboard").then((r) => setData(r.data)).catch(() => setData({ properties: [], totals: {} })); }, []);

  if (!data) return <OwnerLayout title="Dashboard"><p className="text-brand-plum/50">Caricamento...</p></OwnerLayout>;

  const t = data.totals || {};
  const firstName = data.owner?.first_name || "";

  return (
    <OwnerLayout title={`Ciao ${firstName}`.trim() || "Dashboard"}>
      <p className="text-brand-plum/60 -mt-4 mb-6">Ecco come stanno andando le tue strutture su Quattro Accommodations.</p>

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-5 mb-8" data-testid="owner-kpis">
        {KPIS.map((c) => {
          let v = t[c.key];
          if (c.key === "avg_rating") v = v ?? "–";
          else v = v ?? 0;
          return (
            <div key={c.key} className="rounded-2xl bg-white p-5 border border-brand-plum/8 shadow-sm">
              <span className={`w-10 h-10 rounded-xl ${c.bg} flex items-center justify-center`}><c.icon className={c.color} size={18} /></span>
              <p className="font-serif text-3xl text-brand-plum mt-3">{v}{c.suffix && v !== "–" ? c.suffix : ""}</p>
              <p className="text-xs text-brand-plum/50 mt-0.5">{c.label}</p>
            </div>
          );
        })}
      </div>

      <h2 className="font-serif text-xl text-brand-plum mb-4">Le tue strutture</h2>
      <div className="space-y-4" data-testid="owner-properties">
        {data.properties.length === 0 && (
          <div className="rounded-2xl bg-white border border-brand-plum/8 p-10 text-center text-brand-plum/40">
            Nessuna struttura associata al tuo account. Contatta il team Quattro.
          </div>
        )}
        {data.properties.map((p) => (
          <div key={p.id} className="rounded-2xl bg-white border border-brand-plum/8 shadow-sm overflow-hidden" data-testid={`owner-property-${p.slug}`}>
            <div className="flex flex-col sm:flex-row">
              <img src={p.image || "https://via.placeholder.com/300x200"} alt={p.name} className="w-full sm:w-44 h-40 sm:h-auto object-cover" />
              <div className="flex-1 p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-serif text-lg text-brand-plum truncate">{p.name}</p>
                    <p className="text-sm text-brand-plum/50">{p.city} · {p.type}</p>
                  </div>
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full shrink-0 ${p.status === "pubblicata" ? "bg-brand-tealLight text-brand-tealDark" : "bg-amber-50 text-brand-gold"}`}>
                    {p.status === "pubblicata" ? "Online" : "Non pubblicata"}
                  </span>
                </div>
                <div className="grid grid-cols-4 gap-2 mt-5 pt-4 border-t border-brand-plum/8">
                  <Metric label="Visite" value={p.visits} />
                  <Metric label="Verifica disp." value={p.inquiries} />
                  <Metric label="WhatsApp" value={p.whatsapp ?? 0} />
                  <Metric label="Voto" value={p.rating ?? "–"} />
                </div>
                <div className="flex items-center gap-4 mt-4">
                  <a href={`/proprieta/${p.slug}`} target="_blank" rel="noreferrer" data-testid={`owner-view-minisite-${p.slug}`} className="text-sm text-brand-teal hover:text-brand-tealDark flex items-center gap-1.5 font-medium">
                    <ExternalLink size={14} /> Vedi mini-sito
                  </a>
                  {p.reviews_count > 0 && (
                    <button onClick={() => setOpenReviews(openReviews === p.id ? null : p.id)} data-testid={`owner-toggle-reviews-${p.slug}`} className="text-sm text-brand-plum/60 hover:text-brand-plum flex items-center gap-1.5">
                      <Star size={14} className="fill-brand-gold text-brand-gold" /> {p.reviews_count} recensioni
                      <ChevronDown size={14} className={`transition-transform ${openReviews === p.id ? "rotate-180" : ""}`} />
                    </button>
                  )}
                </div>
              </div>
            </div>
            {openReviews === p.id && (
              <div className="bg-brand-warm/40 border-t border-brand-plum/8 p-5 grid sm:grid-cols-2 gap-4" data-testid={`owner-reviews-${p.slug}`}>
                {p.reviews.map((r, i) => (
                  <div key={i} className="rounded-xl bg-white border border-brand-plum/10 p-4">
                    <div className="flex items-center gap-1 mb-1.5">
                      {[...Array(5)].map((_, s) => <Star key={s} size={12} className="fill-brand-gold text-brand-gold" />)}
                      <span className="text-xs text-brand-plum/50 ml-1">{r.rating}</span>
                    </div>
                    <p className="text-sm text-brand-plum/70 italic">"{r.comment}"</p>
                    <p className="text-xs text-brand-plum/50 mt-2 font-medium">{r.author} · {r.date}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </OwnerLayout>
  );
}
